import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowUpRight, ShieldCheck, Zap, HeartHandshake, Sparkles, MessageCircle, ChevronLeft, ChevronRight, Car, Truck, Award, Users, Star,
} from "lucide-react";

function Icon({ name, className }) {
  const I = Icons[name] || Icons.Shield;
  return <I className={className} />;
}

function getUtm() {
  try {
    const raw = localStorage.getItem("asg_utm");
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

const CATEGORIES = [
  { key: "sigorta", label: "Sigorta & Kasko", icon: ShieldCheck },
  { key: "turbo_asist", label: "Turbo Asist", icon: Truck },
];

export default function HomePage() {
  const { openForm } = useOutletContext();
  const { settings } = useSettings();
  const [products, setProducts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [category, setCategory] = useState("sigorta");
  const [productKey, setProductKey] = useState("");
  const [staffId, setStaffId] = useState("");
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [slideIdx, setSlideIdx] = useState(0);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/staff/public").then((r) => setStaff(r.data)).catch(() => {});
  }, []);

  const slides = useMemo(() => {
    const s = settings?.slider_images;
    if (Array.isArray(s) && s.length) return s;
    return [{ url: settings?.hero_image, title: settings?.hero_title, subtitle: settings?.hero_subtitle }];
  }, [settings]);

  useEffect(() => {
    if (slides.length <= 1) return;
    const t = setInterval(() => setSlideIdx((i) => (i + 1) % slides.length), 6000);
    return () => clearInterval(t);
  }, [slides.length]);

  const filteredProducts = useMemo(
    () => products.filter((p) => (p.category || "sigorta") === category),
    [products, category]
  );

  useEffect(() => {
    // Reset product when category changes
    setProductKey("");
    setValues({});
  }, [category]);

  const product = useMemo(() => products.find((p) => p.key === productKey), [products, productKey]);
  const wa = (settings?.whatsapp || "").replace(/[^0-9]/g, "");
  const waLink = wa ? `https://wa.me/${wa}` : "#";

  const setField = (name, v) => setValues((prev) => ({ ...prev, [name]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!productKey) { toast.error("Lütfen bir ürün seçin"); return; }
    if (product) {
      for (const f of product.form_fields || []) {
        if (f.required && !values[f.name]) { toast.error(`${f.label} zorunludur`); return; }
      }
    }
    setSubmitting(true);
    try {
      await api.post("/requests", {
        product_key: productKey,
        staff_id: staffId || null,
        data: values,
        utm: getUtm(),
      });
      try {
        if (settings?.google_ads_id && settings?.google_ads_conversion_label && window.gtag) {
          window.gtag("event", "conversion", { send_to: `${settings.google_ads_id}/${settings.google_ads_conversion_label}` });
        }
        if (window.gtag) window.gtag("event", "generate_lead", { product: productKey });
      } catch {}
      toast.success("Talebiniz alındı. En kısa sürede sizi arayacağız.");
      setProductKey(""); setStaffId(""); setValues({});
    } catch (err) {
      toast.error(apiError(err));
    } finally { setSubmitting(false); }
  };

  return (
    <>
      {/* HERO — HALF PAGE SLIDER + OVERLAYING FORM */}
      <section className="relative overflow-hidden" style={{ background: "var(--asg-bg)" }} data-testid="home-hero">
        <div className="relative h-[62vh] min-h-[520px] lg:h-[72vh] max-h-[820px] w-full overflow-hidden">
          {/* Slides */}
          {slides.map((s, i) => (
            <div
              key={i}
              className={`absolute inset-0 transition-opacity duration-[1200ms] ease-in-out ${i === slideIdx ? "opacity-100" : "opacity-0"}`}
              style={{ background: `url(${s.url}) center/cover no-repeat` }}
              data-testid={`hero-slide-${i}`}
            />
          ))}
          {/* Gradient overlay for readability */}
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 40%, rgba(255,255,255,0.0) 90%)" }} />

          {/* Text overlay left */}
          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 h-full flex items-start pt-16 lg:pt-24">
            <div className="max-w-xl animate-in-up hero-white">
              <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.35em] border border-white/40 rounded-full px-4 py-1.5 mb-6 backdrop-blur-sm bg-white/5" data-testid="hero-badge">
                <Sparkles className="w-3.5 h-3.5" style={{ color: "#F5D97A" }} /> Premium Sigorta Danışmanlığı
              </span>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] drop-shadow-lg" data-testid="hero-title" style={{ color: "#ffffff" }}>
                {slides[slideIdx]?.title || settings.hero_title}
              </h1>
              <p className="mt-5 text-base lg:text-lg max-w-lg leading-relaxed drop-shadow" data-testid="hero-subtitle" style={{ color: "rgba(255,255,255,0.92)" }}>
                {slides[slideIdx]?.subtitle || settings.hero_subtitle}
              </p>
            </div>
          </div>

          {/* Slider controls */}
          {slides.length > 1 && (
            <>
              <button
                onClick={() => setSlideIdx((i) => (i - 1 + slides.length) % slides.length)}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-20 h-11 w-11 rounded-full bg-white/20 hover:bg-white/35 border border-white/30 text-white flex items-center justify-center backdrop-blur transition"
                data-testid="hero-slide-prev"
                aria-label="Önceki"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => setSlideIdx((i) => (i + 1) % slides.length)}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-20 h-11 w-11 rounded-full bg-white/20 hover:bg-white/35 border border-white/30 text-white flex items-center justify-center backdrop-blur transition"
                data-testid="hero-slide-next"
                aria-label="Sonraki"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setSlideIdx(i)}
                    className={`h-1.5 rounded-full transition-all ${i === slideIdx ? "w-10 bg-white" : "w-4 bg-white/50"}`}
                    data-testid={`hero-slide-dot-${i}`}
                    aria-label={`Slayt ${i + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* OVERLAYING REQUEST FORM (turkishairlines style) */}
        <div className="relative z-20 max-w-6xl mx-auto px-4 lg:px-6 -mt-32 lg:-mt-40 pb-16 lg:pb-24">
          <div className="rounded-2xl bg-white shadow-2xl overflow-hidden border border-[color:var(--asg-border)]" data-testid="home-request-form">
            {/* Tabs */}
            <div className="grid grid-cols-2 border-b border-[color:var(--asg-border)]">
              {CATEGORIES.map((c) => {
                const active = category === c.key;
                return (
                  <button
                    key={c.key}
                    onClick={() => setCategory(c.key)}
                    className={`relative flex items-center justify-center gap-3 py-5 lg:py-6 text-sm lg:text-base font-medium transition-all
                      ${active ? "text-[color:var(--asg-text)]" : "text-[color:var(--asg-muted)] hover:text-[color:var(--asg-text)]"}`}
                    style={active ? { background: "linear-gradient(180deg, #ffffff 0%, #fdf7e6 100%)" } : {}}
                    data-testid={`home-tab-${c.key}`}
                  >
                    <c.icon className={`w-5 h-5 ${active ? "asg-primary" : ""}`} />
                    <span className="font-heading tracking-wide">{c.label}</span>
                    {active && (
                      <span className="absolute bottom-0 left-1/2 -translate-x-1/2 h-1 w-24 rounded-t"
                        style={{ background: "linear-gradient(90deg, transparent, var(--asg-primary), transparent)" }} />
                    )}
                  </button>
                );
              })}
            </div>

            <form onSubmit={submit} className="p-6 lg:p-8">
              <div className="grid md:grid-cols-2 gap-4 mb-5">
                <div className="grid gap-2">
                  <Label className="text-[color:var(--asg-muted)] text-xs uppercase tracking-widest">
                    {category === "turbo_asist" ? "Hizmet Türü" : "Sigorta Türü"} <span className="asg-primary">*</span>
                  </Label>
                  <Select value={productKey} onValueChange={setProductKey}>
                    <SelectTrigger className="h-12 bg-[color:var(--asg-surface)] border-[color:var(--asg-border)] text-[color:var(--asg-text)]" data-testid="home-product-select">
                      <SelectValue placeholder={filteredProducts.length ? "Seçin" : "Bu sekmede ürün yok"} />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[color:var(--asg-border)]">
                      {filteredProducts.map((p) => (
                        <SelectItem key={p.key} value={p.key} data-testid={`home-product-${p.key}`}>{p.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label className="text-[color:var(--asg-muted)] text-xs uppercase tracking-widest">Görüşmek İstediğiniz Personel</Label>
                  <Select value={staffId} onValueChange={setStaffId}>
                    <SelectTrigger className="h-12 bg-[color:var(--asg-surface)] border-[color:var(--asg-border)] text-[color:var(--asg-text)]" data-testid="home-staff-select">
                      <SelectValue placeholder="Fark etmez" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[color:var(--asg-border)]">
                      {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {product && (
                <>
                  <hr className="gold-hr my-6" />
                  <div className="grid md:grid-cols-2 gap-4">
                    {(product.form_fields || []).map((f) => (
                      <div key={f.name} className={f.type === "textarea" ? "md:col-span-2 grid gap-2" : "grid gap-2"}>
                        <Label className="text-[color:var(--asg-muted)] text-xs uppercase tracking-widest">
                          {f.label} {f.required && <span className="asg-primary">*</span>}
                        </Label>
                        {f.type === "textarea" ? (
                          <Textarea
                            value={values[f.name] || ""}
                            onChange={(e) => setField(f.name, e.target.value)}
                            className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)] text-[color:var(--asg-text)] min-h-[80px]"
                            data-testid={`home-field-${f.name}`}
                          />
                        ) : f.type === "select" ? (
                          <Select value={values[f.name] || ""} onValueChange={(v) => setField(f.name, v)}>
                            <SelectTrigger className="h-11 bg-[color:var(--asg-surface)] border-[color:var(--asg-border)] text-[color:var(--asg-text)]" data-testid={`home-field-${f.name}`}>
                              <SelectValue placeholder="Seçiniz" />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-[color:var(--asg-border)]">
                              {(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            type={f.type || "text"}
                            value={values[f.name] || ""}
                            onChange={(e) => setField(f.name, e.target.value)}
                            className="h-11 bg-[color:var(--asg-surface)] border-[color:var(--asg-border)] text-[color:var(--asg-text)]"
                            data-testid={`home-field-${f.name}`}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}

              <div className="mt-7 flex flex-wrap items-center justify-end gap-3">
                <Button
                  type="submit"
                  disabled={submitting || !productKey}
                  className="asg-btn-primary rounded-full h-12 px-8 text-base font-semibold border-0"
                  data-testid="home-form-submit"
                >
                  {submitting ? "Gönderiliyor..." : (
                    <span className="inline-flex items-center gap-2">Teklif Talep Et <ArrowUpRight className="w-4 h-4" /></span>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* PRODUCTS — directly under the request form, full-width */}
      <section className="relative py-16 lg:py-24 bg-[color:var(--asg-surface)] border-y border-[color:var(--asg-border)]" data-testid="home-products-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-6">
            <div>
              <span className="text-xs uppercase tracking-[0.3em] asg-primary font-semibold">Sigorta Ürünlerimiz</span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold mt-3 tracking-tight text-[color:var(--asg-text)]" data-testid="products-heading">
                Her ihtiyaca özel <span className="asg-gold-text">güvence</span>
              </h2>
            </div>
            <p className="text-[color:var(--asg-muted)] max-w-md text-sm leading-relaxed">
              Aracınızdan konutunuza, sağlığınızdan seyahatlerinize kadar; hayatınızın her alanında yanınızdayız.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p, idx) => (
              <div key={p.key} className="asg-card rounded-2xl overflow-hidden group relative" data-testid={`product-card-${p.key}`}>
                <div className="relative h-48 overflow-hidden">
                  <img src={p.image_url} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.55) 100%)" }} />
                  <div className="absolute top-4 left-4 h-11 w-11 rounded-xl bg-white/95 border border-[color:var(--asg-border)] flex items-center justify-center">
                    <Icon name={p.icon} className="w-5 h-5" style={{ color: "var(--asg-primary-dark)" }} />
                  </div>
                  <span className="absolute top-4 right-4 text-[10px] uppercase tracking-widest text-white border border-white/30 rounded-full px-3 py-1 bg-black/30 backdrop-blur">
                    {p.category === "turbo_asist" ? "Turbo Asist" : `#${String(idx + 1).padStart(2, "0")}`}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-semibold text-[color:var(--asg-text)]">{p.title}</h3>
                  <p className="text-sm text-[color:var(--asg-muted)] mt-2 leading-relaxed line-clamp-3">{p.short_description}</p>
                  <button
                    onClick={() => openForm(p.key)}
                    className="mt-5 inline-flex items-center gap-2 text-sm font-medium group/btn"
                    style={{ color: "var(--asg-text)" }}
                    data-testid={`product-cta-${p.key}`}
                  >
                    <span className="border-b border-[color:var(--asg-primary)] pb-0.5">Teklif Al</span>
                    <ArrowUpRight className="w-4 h-4 asg-primary transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="relative py-20 border-t border-[color:var(--asg-border)] bg-[color:var(--asg-surface)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <span className="text-xs uppercase tracking-[0.3em] asg-primary font-semibold">Neden Aydınlar Global?</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold mt-3 tracking-tight leading-[1.1] text-[color:var(--asg-text)]">
              Sigortada <span className="asg-gold-text">premium</span> deneyim
            </h2>
            <p className="text-[color:var(--asg-muted)] mt-6 leading-relaxed">
              Sadece bir poliçe satıcısı değil; kaza anında, sağlık gündeminde, seyahat rezervasyonunda
              her an yanınızda olan gerçek bir danışmanız.
            </p>
            <Link to="/hakkimizda"
              className="inline-flex items-center gap-2 mt-8 text-sm text-[color:var(--asg-text)] border-b border-[color:var(--asg-primary)] pb-1 hover:opacity-80"
              data-testid="whyus-about-link"
            >
              Hakkımızda Detay <ArrowUpRight className="w-4 h-4 asg-primary" />
            </Link>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {[
              { icon: ShieldCheck, title: "Bağımsız Danışmanlık", desc: "20+ sigorta şirketiyle çalışıyor, size en uygun teklifi seçiyoruz." },
              { icon: Zap, title: "Hızlı Hasar Süreci", desc: "24 saatte hasar dosyası, en kısa sürede ödeme takibi." },
              { icon: HeartHandshake, title: "Uçtan Uca Destek", desc: "Poliçe sonrası da yanınızdayız — her adımı biz yönetiyoruz." },
              { icon: Sparkles, title: "Premium Portföy", desc: "Kurumsal ve VIP müşteriler için özel teminat paketleri." },
            ].map((f) => (
              <div key={f.title} className="asg-card rounded-2xl p-6" data-testid={`whyus-${f.title}`}>
                <div className="h-11 w-11 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: "linear-gradient(135deg, rgba(212,175,55,0.15), rgba(183,110,121,0.08))" }}>
                  <f.icon className="w-5 h-5" style={{ color: "var(--asg-primary-dark)" }} />
                </div>
                <h3 className="font-heading text-lg font-semibold text-[color:var(--asg-text)]">{f.title}</h3>
                <p className="text-sm text-[color:var(--asg-muted)] mt-1.5 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl p-10 md:p-16 border border-[color:var(--asg-border)]"
            style={{ background: "linear-gradient(120deg, #ffffff 0%, #fdf7e6 60%, rgba(212,175,55,0.25) 100%)" }}>
            <div className="max-w-2xl">
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[color:var(--asg-text)]">
                Bugün <span className="asg-gold-text">ücretsiz</span> teklifinizi alın
              </h2>
              <p className="text-[color:var(--asg-muted)] mt-4 leading-relaxed">
                2 dakikada talep formunu doldurun, uzman danışmanımız size özel teklifi hazırlasın.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <button
                  onClick={() => openForm(null)}
                  className="asg-btn-primary inline-flex items-center h-12 px-7 rounded-full font-semibold"
                  data-testid="cta-request-btn"
                >
                  Teklif Formu
                </button>
                <a href={waLink} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 h-12 px-7 rounded-full border border-emerald-500/50 text-emerald-700 hover:bg-emerald-50 transition"
                  data-testid="cta-whatsapp-btn"
                >
                  <MessageCircle className="w-5 h-5" /> WhatsApp'ta Yaz
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
